import {
  LayoutDashboard, Users, CalendarDays, Briefcase,
  GraduationCap, DollarSign, Shield, UserPlus, Bell, Building2,
  Settings, LogOut, User, UserCheck, FileText, BarChart3,
  ClipboardList, Target, Globe, ReceiptText,
  Activity, Database, KeyRound, Cog, MessageSquare, ChevronDown, Users2, Bot, HelpCircle, ShieldAlert, ShieldCheck, ListTodo, HeartPulse, Package, Clock, type LucideIcon
} from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect, useState, useRef } from "react";
import {
  apiGetOnboardingThreads, apiGetNavPermissions, apiGetTeamJoinRequestsPendingCount,
  apiGetTenantConfig, apiGetWorkflowTaskCount,
  apiGetPendingSurveysCount, apiGetPendingOnboardingCount, apiGetPendingDocumentsCount,
  apiGetPendingLeaveCount, apiGetUnreadNotificationsCount, apiGetPendingOffboardingCount,
  apiGetMyOnboarding, apiGetMyPendingExpensesCount, apiGetAdminPendingExpensesCount,
  apiGetPendingRecruitmentCount, apiGetPendingPayrollCount, apiGetAdminPendingOnboardingCount,
  apiGetConcernOpenCount, apiGetCompanyLogo, getTenantSlug, API_BASE,
  type NavVisibilitySetting,
} from "@/lib/api";
import { ALWAYS_ON_MODULES, isModuleEnabled } from "@/lib/modules";
import { useQuery } from "@tanstack/react-query";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  useAuth,
  getRoleI18nKey,
  type MainRole,
  type UserSubRole,
} from "@/contexts/AuthContext";

interface NavItem {
  /** i18n key under "nav" namespace */
  titleKey: string;
  url: string;
  icon: LucideIcon;
  /** Optional unread / count badge */
  badge?: number;
  /** Module key to gate visibility on, when it differs from titleKey. */
  moduleKey?: string;
}

// =========================================
// Navigation definitions per role
// =========================================

// =========================================
// Shared main nav – every role sees the same core items
// =========================================
const commonMainNav: NavItem[] = [
  { titleKey: "dashboard", url: "/", icon: LayoutDashboard },
  { titleKey: "myProfile", url: "/my-profile", icon: User },
  { titleKey: "peopleDirectory", url: "/employees", icon: Users },
  { titleKey: "leaveManagement", url: "/leave", icon: CalendarDays },
  { titleKey: "timekeeping", url: "/timekeeping", icon: Clock },
  { titleKey: "careerDevelopment", url: "/performance", icon: GraduationCap },
  { titleKey: "salary", url: "/my-salary", icon: DollarSign },
  { titleKey: "myExpenses", url: "/my-expenses", icon: ReceiptText },
  { titleKey: "myDocuments", url: "/my-documents", icon: FileText },
  { titleKey: "myTasks", url: "/my-tasks", icon: ListTodo },
  { titleKey: "myOnboarding", url: "/my-onboarding", icon: UserPlus },
  { titleKey: "mySurveys", url: "/my-surveys", icon: ClipboardList },
  { titleKey: "raiseConcern", url: "/raise-concern", icon: ShieldAlert },
  { titleKey: "aiAssistant", url: "/ai-assistant", icon: Bot },
  { titleKey: "saraHelp", url: "/sara-help", icon: HelpCircle },
];

// =========================================
// Administration nav – role-specific
// =========================================

const adminAdminNav: NavItem[] = [
  { titleKey: "organization", url: "/organization", icon: Building2 },
  { titleKey: "teamLeave", url: "/team-leave", icon: ClipboardList },
  { titleKey: "recruitment", url: "/recruitment", icon: Briefcase },
  { titleKey: "hrDocuments", url: "/documents", icon: FileText },
  { titleKey: "payroll", url: "/payroll", icon: DollarSign },
  { titleKey: "onboarding", url: "/onboarding", icon: UserPlus },
  { titleKey: "workflows", url: "/workflows", icon: Activity },
  { titleKey: "timekeepingAdmin", url: "/timekeeping-admin", icon: Clock, moduleKey: "timekeeping" },
  { titleKey: "expenses", url: "/expenses", icon: ReceiptText },
  { titleKey: "equipmentManagement", url: "/equipment", icon: Package },
  { titleKey: "medicalCheckups", url: "/medical-checkups", icon: HeartPulse },
  { titleKey: "surveys", url: "/surveys", icon: ClipboardList },
  { titleKey: "hrOpenCases", url: "/hr-cases", icon: ShieldAlert },
  { titleKey: "certifications", url: "/certifications", icon: GraduationCap },
  { titleKey: "corporateEvents", url: "/corporate-events", icon: CalendarDays },
  { titleKey: "gdprRetention", url: "/gdpr-retention", icon: ShieldCheck },
  { titleKey: "hrAnalytics", url: "/hr-analytics", icon: BarChart3 },
  { titleKey: "fieldPermissions", url: "/field-permissions", icon: KeyRound },
  { titleKey: "navPermissions", url: "/nav-permissions", icon: KeyRound },
  { titleKey: "auditLog", url: "/audit-log", icon: Database },
];

const hrAdminNav: NavItem[] = [
  { titleKey: "organization", url: "/organization", icon: Building2 },
  { titleKey: "teamLeave", url: "/team-leave", icon: ClipboardList },
  { titleKey: "recruitment", url: "/recruitment", icon: Briefcase },
  { titleKey: "hrDocuments", url: "/documents", icon: FileText },
  { titleKey: "payroll", url: "/payroll", icon: DollarSign },
  { titleKey: "onboarding", url: "/onboarding", icon: UserPlus },
  { titleKey: "workflows", url: "/workflows", icon: Activity },
  { titleKey: "timekeepingAdmin", url: "/timekeeping-admin", icon: Clock, moduleKey: "timekeeping" },
  { titleKey: "expenses", url: "/expenses", icon: ReceiptText },
  { titleKey: "equipmentManagement", url: "/equipment", icon: Package },
  { titleKey: "medicalCheckups", url: "/medical-checkups", icon: HeartPulse },
  { titleKey: "surveys", url: "/surveys", icon: ClipboardList },
  { titleKey: "hrOpenCases", url: "/hr-cases", icon: ShieldAlert },
  { titleKey: "certifications", url: "/certifications", icon: GraduationCap },
  { titleKey: "corporateEvents", url: "/corporate-events", icon: CalendarDays },
  { titleKey: "gdprRetention", url: "/gdpr-retention", icon: ShieldCheck },
  { titleKey: "hrAnalytics", url: "/hr-analytics", icon: BarChart3 },
];

// --- Line Manager team nav (shown if isLineManager flag is true) ---
const lineManagerTeamNav: NavItem[] = [
  { titleKey: "teamOverview", url: "/team-overview", icon: Users },
  { titleKey: "certifications", url: "/certifications", icon: GraduationCap },
];

const cLevelAdminNav: NavItem[] = [
  { titleKey: "organization", url: "/organization", icon: Globe },
  { titleKey: "payroll", url: "/payroll", icon: DollarSign },
  { titleKey: "hrAnalytics", url: "/hr-analytics", icon: BarChart3 },
];

// =========================================
// Get nav for current role
// =========================================
function getNavForRole(
  mainRole: MainRole,
  subRole?: UserSubRole,
  isLineManager?: boolean,
): {
  main: NavItem[];
  secondary: NavItem[];
  secondaryLabelKey: string;
  teamNav: NavItem[];
} {
  const teamNav = isLineManager ? lineManagerTeamNav : [];

  if (mainRole === "admin") {
    return {
      main: commonMainNav,
      secondary: adminAdminNav,
      secondaryLabelKey: "administration",
      teamNav,
    };
  }
  if (mainRole === "hr") {
    return {
      main: commonMainNav,
      secondary: hrAdminNav,
      secondaryLabelKey: "administration",
      teamNav,
    };
  }

  switch (subRole) {
    case "c_level":
      return {
        main: commonMainNav,
        secondary: cLevelAdminNav,
        secondaryLabelKey: "administration",
        teamNav,
      };
    case "employee":
    default:
      return {
        main: commonMainNav,
        secondary: [],
        secondaryLabelKey: "",
        teamNav,
      };
  }
}

const bottomNav: NavItem[] = [
  { titleKey: "messages", url: "/messages", icon: MessageSquare },
  { titleKey: "notifications", url: "/notifications", icon: Bell },
  { titleKey: "settings", url: "/settings", icon: Settings },
];

// =========================================
// Sidebar component
// =========================================

export function AppSidebar() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data: logoUrl } = useQuery({
    queryKey: ["company-logo", getTenantSlug()],
    queryFn: apiGetCompanyLogo,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    const restoreScroll = () => {
      const savedScroll = localStorage.getItem("sidebar-scroll");
      if (savedScroll && scrollRef.current) {
        scrollRef.current.scrollTop = parseInt(savedScroll, 10);
      }
    };

    // Restore immediately and after a short tick
    restoreScroll();
    const timer = setTimeout(restoreScroll, 10);
    return () => clearTimeout(timer);
  }, [pathname]);

  const handleScroll = () => {
    if (scrollRef.current) {
      localStorage.setItem("sidebar-scroll", scrollRef.current.scrollTop.toString());
    }
  };

  const [isMainOpen, setIsMainOpen] = useState(() => {
    return localStorage.getItem("sidebar-main-open") !== "false";
  });
  const [isSecondaryOpen, setIsSecondaryOpen] = useState(() => {
    return localStorage.getItem("sidebar-secondary-open") === "true";
  });
  const [isTeamOpen, setIsTeamOpen] = useState(() => {
    return localStorage.getItem("sidebar-team-open") === "true";
  });

  const handleMainOpenChange = (open: boolean) => {
    setIsMainOpen(open);
    localStorage.setItem("sidebar-main-open", open.toString());
  };

  const handleSecondaryOpenChange = (open: boolean) => {
    setIsSecondaryOpen(open);
    localStorage.setItem("sidebar-secondary-open", open.toString());
  };

  const handleTeamOpenChange = (open: boolean) => {
    setIsTeamOpen(open);
    localStorage.setItem("sidebar-team-open", open.toString());
  };

  // The product tour expands every nav section so its steps can highlight items
  // that the user may have collapsed.
  useEffect(() => {
    const openAll = () => {
      handleMainOpenChange(true);
      handleSecondaryOpenChange(true);
      handleTeamOpenChange(true);
    };
    window.addEventListener("besthr:tour-open-sections", openAll);
    return () => window.removeEventListener("besthr:tour-open-sections", openAll);
  }, []);

  // Unread messages count for the Messages nav badge
  const [unreadMessages, setUnreadMessages] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const threads = await apiGetOnboardingThreads();
        if (!cancelled) {
          setUnreadMessages(threads.reduce((sum, t) => sum + (t.unreadCount ?? 0), 0));
        }
      } catch { /* ignore */ }
    };
    load();
    const interval = setInterval(load, 30_000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  // Pending team join requests count (for badges)
  const { data: pendingJoinData } = useQuery({
    queryKey: ["team-join-pending-count"],
    queryFn: apiGetTeamJoinRequestsPendingCount,
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
  const pendingJoinCount = pendingJoinData?.count ?? 0;

  // Pending workflow task count (for My Tasks badge)
  const { data: pendingTaskData } = useQuery({
    queryKey: ["workflow-task-count"],
    queryFn: apiGetWorkflowTaskCount,
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
  const pendingTaskCount = pendingTaskData?.count ?? 0;

  const { data: pendingSurveysData } = useQuery({
    queryKey: ["pending-surveys-count"],
    queryFn: apiGetPendingSurveysCount,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const pendingSurveysCount = pendingSurveysData?.count ?? 0;

  const { data: pendingOnboardingData } = useQuery({
    queryKey: ["pending-onboarding-count"],
    queryFn: apiGetPendingOnboardingCount,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const pendingOnboardingCount = pendingOnboardingData?.count ?? 0;

  const { data: myOnboardingSession, isLoading: myOnboardingLoading } = useQuery({
    queryKey: ["my-onboarding-session"],
    queryFn: apiGetMyOnboarding,
    refetchInterval: 5 * 60_000,
    staleTime: 2 * 60_000,
  });
  const hasActiveOnboarding = !!myOnboardingSession;

  const { data: pendingDocsData } = useQuery({
    queryKey: ["pending-documents-count"],
    queryFn: apiGetPendingDocumentsCount,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const pendingDocsCount = pendingDocsData?.count ?? 0;

  const { data: pendingLeaveData } = useQuery({
    queryKey: ["pending-leave-count"],
    queryFn: apiGetPendingLeaveCount,
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
  const pendingLeaveCount = pendingLeaveData?.count ?? 0;

  const { data: unreadNotifsData } = useQuery({
    queryKey: ["unread-notifications-count"],
    queryFn: apiGetUnreadNotificationsCount,
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
  const unreadNotifsCount = unreadNotifsData?.count ?? 0;

  const { data: pendingOffboardingData } = useQuery({
    queryKey: ["pending-offboarding-count"],
    queryFn: apiGetPendingOffboardingCount,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const pendingOffboardingCount = pendingOffboardingData?.count ?? 0;

  const { data: myPendingExpensesData } = useQuery({
    queryKey: ["my-pending-expenses-count"],
    queryFn: apiGetMyPendingExpensesCount,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const myPendingExpensesCount = myPendingExpensesData?.count ?? 0;

  const { data: adminPendingExpensesData } = useQuery({
    queryKey: ["admin-pending-expenses-count"],
    queryFn: apiGetAdminPendingExpensesCount,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const adminPendingExpensesCount = adminPendingExpensesData?.count ?? 0;

  // Tenant module config — also drives which badge queries are allowed to fire,
  // so we don't poll endpoints the backend now 403s for disabled modules.
  const { data: tenantConfig } = useQuery({
    queryKey: ["tenant-config"],
    queryFn: apiGetTenantConfig,
    staleTime: 10 * 60 * 1000,
  });
  const isModuleOn = (key: string) => {
    const modules = tenantConfig?.enabledModules;
    if (!modules) return true; // null = all enabled
    return isModuleEnabled(modules, key);
  };

  const { data: pendingRecruitmentData } = useQuery({
    queryKey: ["pending-recruitment-count"],
    queryFn: apiGetPendingRecruitmentCount,
    refetchInterval: 60_000,
    staleTime: 30_000,
    enabled: isModuleOn("recruitment"),
  });
  const pendingRecruitmentCount = pendingRecruitmentData?.count ?? 0;

  const { data: pendingPayrollData } = useQuery({
    queryKey: ["pending-payroll-count"],
    queryFn: apiGetPendingPayrollCount,
    refetchInterval: 120_000,
    staleTime: 60_000,
    enabled: isModuleOn("payroll"),
  });
  const pendingPayrollCount = pendingPayrollData?.count ?? 0;

  const { data: adminPendingOnboardingData } = useQuery({
    queryKey: ["admin-pending-onboarding-count"],
    queryFn: apiGetAdminPendingOnboardingCount,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const adminPendingOnboardingCount = adminPendingOnboardingData?.count ?? 0;

  const mainRole = user?.role.mainRole ?? "hr";
  const subRole = user?.role.subRole;
  const isHrOrAdmin = mainRole === "admin" || mainRole === "hr";

  const { data: concernOpenCountData } = useQuery({
    queryKey: ["concern-open-count"],
    queryFn: apiGetConcernOpenCount,
    refetchInterval: 60_000,
    staleTime: 30_000,
    enabled: isHrOrAdmin && isModuleOn("hrOpenCases"),
  });
  const concernOpenCount = concernOpenCountData?.openCount ?? 0;
  const isLineManager = !!user?.isLineManager;

  const { main, secondary, secondaryLabelKey, teamNav } = getNavForRole(
    mainRole,
    subRole,
    user?.isLineManager,
  );

  const { data: navPerms = [] } = useQuery({
    queryKey: ["nav-permissions"],
    queryFn: apiGetNavPermissions,
    staleTime: 5 * 60 * 1000,
  });

  function filterByEnabledModules(items: NavItem[]): NavItem[] {
    const modules = tenantConfig?.enabledModules;
    if (!modules) return items; // null = all enabled
    return items.filter((item) => {
      const key = item.moduleKey ?? item.titleKey;
      return isModuleEnabled(modules, key);
    });
  }

  function getVisibilityKey(perm: NavVisibilitySetting): boolean {
    if (mainRole === "admin") return perm.visibleToAdmin;
    if (mainRole === "hr") return perm.visibleToHr;
    // Check sub-roles before isLineManager so a c_level user who is also a
    // line manager gets the c_level visibility (not the more restrictive LM one)
    if (subRole === "c_level") return perm.visibleToCLevel;
    if (user?.isLineManager) return perm.visibleToLineManager;
    return perm.visibleToUser;
  }

  function filterByNavPerms(items: NavItem[], section: NavVisibilitySetting["section"]): NavItem[] {
    if (navPerms.length === 0) return items;
    return items.filter((item) => {
      const perm = navPerms.find(
        (p) => p.navKey === item.titleKey && p.section === section,
      );
      return perm ? getVisibilityKey(perm) : true;
    });
  }

  // Inject pending-action badges into nav items
  const mainWithBadges = main.map((item) => {
    if (item.titleKey === "mySurveys" && pendingSurveysCount > 0)
      return { ...item, badge: pendingSurveysCount };
    if (item.titleKey === "myOnboarding" && pendingOnboardingCount > 0)
      return { ...item, badge: pendingOnboardingCount };
    if (item.titleKey === "myDocuments" && pendingDocsCount > 0 && !isHrOrAdmin)
      return { ...item, badge: pendingDocsCount };
    if (item.titleKey === "myTasks" && pendingTaskCount > 0)
      return { ...item, badge: pendingTaskCount };
    if (item.titleKey === "myExpenses" && myPendingExpensesCount > 0)
      return { ...item, badge: myPendingExpensesCount };
    return item;
  });

  const secondaryWithBadges = secondary.map((item) => {
    if (item.titleKey === "teamLeave" && pendingLeaveCount > 0)
      return { ...item, badge: pendingLeaveCount };
    if (item.titleKey === "recruitment" && pendingRecruitmentCount > 0 && isHrOrAdmin)
      return { ...item, badge: pendingRecruitmentCount };
    if (item.titleKey === "payroll" && pendingPayrollCount > 0 && isHrOrAdmin)
      return { ...item, badge: pendingPayrollCount };
    if (item.titleKey === "expenses" && adminPendingExpensesCount > 0 && isHrOrAdmin)
      return { ...item, badge: adminPendingExpensesCount };
    if (item.titleKey === "hrDocuments" && pendingDocsCount > 0 && isHrOrAdmin)
      return { ...item, badge: pendingDocsCount };
    if (item.titleKey === "onboarding" && isHrOrAdmin) {
      const combined = pendingOffboardingCount + adminPendingOnboardingCount;
      if (combined > 0) return { ...item, badge: combined };
    }
    if (item.titleKey === "hrOpenCases" && isHrOrAdmin && concernOpenCount > 0)
      return { ...item, badge: concernOpenCount };
    return item;
  });

  const teamNavWithBadges = teamNav.map((item) => {
    if (item.titleKey === "approvals" && isLineManager && pendingJoinCount > 0)
      return { ...item, badge: pendingJoinCount };
    if (item.titleKey === "teamLeave" && pendingLeaveCount > 0)
      return { ...item, badge: pendingLeaveCount };
    return item;
  });

  // Hide "My Onboarding" until the query resolves AND there is an active session
  const filteredMain = myOnboardingLoading
    ? mainWithBadges
    : mainWithBadges.filter((item) => item.titleKey !== "myOnboarding" || hasActiveOnboarding);

  const filteredSecondary = filterByNavPerms(filterByEnabledModules(secondaryWithBadges), "administration");
  const filteredTeamNav = filterByNavPerms(filterByEnabledModules(teamNavWithBadges), "teamManagement");

  const roleLabel = user ? t(getRoleI18nKey(user.role)) : "";
  const avatarUrl = user?.avatarUrl;
  const avatarGradient = user?.avatarColor ?? "from-purple-500 to-indigo-500";
  const initials = user?.initials ?? "?";
  const userName = user?.name ?? "User";

  const handleSignOut = async () => {
    // logout() now handles server-side revocation + localStorage cleanup.
    await logout();
    navigate("/login");
  };

  const bottomNav: NavItem[] = [
    { titleKey: "messages", url: "/messages", icon: MessageSquare, badge: unreadMessages || undefined },
    { titleKey: "notifications", url: "/notifications", icon: Bell, badge: unreadNotifsCount || undefined },
    { titleKey: "settings", url: "/settings", icon: Settings },
  ];

  const renderNavItem = (item: NavItem) => {
    const title = t(`nav.${item.titleKey}`);
    return (
      <SidebarMenuItem key={item.titleKey + item.url}>
        <SidebarMenuButton asChild tooltip={title}>
          <NavLink
            to={item.url}
            end={item.url === "/"}
            data-tour={`nav-${item.titleKey}`}
            className="flex items-center gap-2 rounded-md text-sm text-sidebar-foreground hover:bg-sidebar-accent transition-colors"
            activeClassName="bg-sidebar-accent text-sidebar-accent-foreground font-medium"
          >
            <item.icon className="h-4 w-4 shrink-0" />
            <span className="flex-1">{title}</span>
            {item.badge != null && item.badge > 0 && (
              <span className="ml-auto inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none">
                {item.badge > 99 ? "99+" : item.badge}
              </span>
            )}
          </NavLink>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  return (
    <Sidebar collapsible="icon" className="border-r border-sidebar-border">
      <SidebarHeader className="border-b border-sidebar-border">
        <div className="flex items-center justify-center px-3 py-3 group-data-[collapsible=icon]:px-0">
          {logoUrl ? (
            <img
              src={logoUrl.startsWith("http") ? logoUrl : `${API_BASE}${logoUrl}`}
              alt="Company logo"
              className="h-16 max-w-[180px] object-contain group-data-[collapsible=icon]:h-8 group-data-[collapsible=icon]:max-w-[40px]"
            />
          ) : (
            <img
              src="/besthr.png"
              alt="BestHR"
              className="h-10 w-10 rounded-lg shrink-0 object-contain"
            />
          )}
        </div>
      </SidebarHeader>

      <SidebarContent ref={scrollRef} onScroll={handleScroll}>
        {/* Sticky Dashboard link — always visible regardless of scroll */}
        <div className="sticky top-0 z-10 bg-sidebar px-2 pt-2 pb-1 border-b border-sidebar-border">
          <SidebarMenu>{renderNavItem(filteredMain[0])}</SidebarMenu>
        </div>

        <Collapsible
          open={isMainOpen}
          onOpenChange={handleMainOpenChange}
          className="group/collapsible"
        >
          <SidebarGroup>
            <SidebarGroupLabel asChild className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground cursor-pointer transition-colors w-full flex items-center justify-between">
              <CollapsibleTrigger data-tour="section-main">
                <span>{t("nav.main")}</span>
                <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
              </CollapsibleTrigger>
            </SidebarGroupLabel>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>{filterByEnabledModules(filteredMain).slice(1).map(renderNavItem)}</SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>

        {filteredSecondary.length > 0 && (
          <Collapsible
            open={isSecondaryOpen}
            onOpenChange={handleSecondaryOpenChange}
            className="group/collapsible"
          >
            <SidebarGroup>
              <SidebarGroupLabel asChild className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground cursor-pointer transition-colors w-full flex items-center justify-between">
                <CollapsibleTrigger data-tour="section-administration">
                  <span>{t(`nav.${secondaryLabelKey}`)}</span>
                  <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                </CollapsibleTrigger>
              </SidebarGroupLabel>
              <CollapsibleContent>
                <SidebarGroupContent>
                  <SidebarMenu>{filteredSecondary.map(renderNavItem)}</SidebarMenu>
                </SidebarGroupContent>
              </CollapsibleContent>
            </SidebarGroup>
          </Collapsible>
        )}

        {filteredTeamNav.length > 0 && (
          <Collapsible
            open={isTeamOpen}
            onOpenChange={handleTeamOpenChange}
            className="group/collapsible"
          >
            <SidebarGroup>
              <SidebarGroupLabel asChild className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground cursor-pointer transition-colors w-full flex items-center justify-between">
                <CollapsibleTrigger>
                  <span>{t("nav.teamManagement")}</span>
                  <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                </CollapsibleTrigger>
              </SidebarGroupLabel>
              <CollapsibleContent>
                <SidebarGroupContent>
                  <SidebarMenu>{filteredTeamNav.map(renderNavItem)}</SidebarMenu>
                </SidebarGroupContent>
              </CollapsibleContent>
            </SidebarGroup>
          </Collapsible>
        )}
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border">
        <SidebarMenu>{filterByEnabledModules(bottomNav).map(renderNavItem)}</SidebarMenu>

        {/* User card - expanded view */}
        <div className="mt-1 group-data-[collapsible=icon]:hidden">
          <div className="p-3 rounded-lg bg-secondary flex items-center gap-3">
            <EmployeeAvatar
              avatarUrl={avatarUrl}
              avatarColor={avatarGradient}
              initials={initials}
              name={userName}
              size="sm"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">
                {userName}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {roleLabel}
              </p>
            </div>
          </div>
          <button
            onClick={handleSignOut}
            className="mt-2 w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-muted-foreground hover:text-foreground hover:bg-sidebar-accent transition-colors"
          >
            <LogOut className="h-3.5 w-3.5" />
            <span>{t("common.signOut")}</span>
          </button>
        </div>

        {/* User avatar - collapsed view */}
        {isCollapsed && (
          <div className="flex justify-center py-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleSignOut}
                  className="hover:opacity-80 transition-opacity"
                >
                  <EmployeeAvatar
                    avatarUrl={avatarUrl}
                    avatarColor={avatarGradient}
                    initials={initials}
                    name={userName}
                    size="sm"
                  />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">
                {userName} &middot; {t("common.signOut")}
              </TooltipContent>
            </Tooltip>
          </div>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}

